import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteTreasuryPeriod,
  listTreasuryPeriods,
} from "../api/cierresTesoreriaApi";

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    "No se pudo completar la operación."
  );
}

export default function useTesoreriaCierres(filters) {
  const [draftClosures, setDraftClosures] = useState([]);
  const [completedClosures, setCompletedClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(
    filters?.search || ""
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(filters?.search || "");
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [filters?.search]);

  const normalizedFilters = useMemo(
    () => ({
      dateFrom: filters?.dateFrom || "",
      dateTo: filters?.dateTo || "",
      search: debouncedSearch,
    }),
    [filters?.dateFrom, filters?.dateTo, debouncedSearch]
  );

  const fetchClosures = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [drafts, completed] = await Promise.all([
        listTreasuryPeriods({ ...normalizedFilters, status: "draft" }),
        listTreasuryPeriods({ ...normalizedFilters, status: "closed" }),
      ]);

      setDraftClosures(drafts);
      setCompletedClosures(completed);
    } catch (err) {
      setDraftClosures([]);
      setCompletedClosures([]);
      setError(errorToMessage(err));
    } finally {
      setLoading(false);
    }
  }, [normalizedFilters]);

  useEffect(() => {
    fetchClosures();
  }, [fetchClosures]);

  const removeDraftClosure = useCallback(async (periodId) => {
    setDeletingId(String(periodId));
    setError("");
    setSuccessMessage("");

    try {
      await deleteTreasuryPeriod(periodId);
      setDraftClosures((prev) => prev.filter((closure) => String(closure.id) !== String(periodId)));
      setSuccessMessage("Cierre eliminado correctamente.");
    } catch (err) {
      setError(errorToMessage(err));
      throw err;
    } finally {
      setDeletingId("");
    }
  }, []);

  const updateClosure = useCallback((periodId, changes) => {
    const applyChanges = (closures) =>
      closures.map((closure) =>
        String(closure.id) === String(periodId)
          ? { ...closure, ...changes }
          : closure
      );

    setDraftClosures(applyChanges);
    setCompletedClosures(applyChanges);
  }, []);

  return {
    draftClosures,
    completedClosures,
    loading,
    error,
    deletingId,
    successMessage,
    setSuccessMessage,
    refetch: fetchClosures,
    removeDraftClosure,
    updateClosure,
  };
}
